import React from 'react';
import { Card, Form } from '@duxton-web/ui';
import { produce } from 'immer';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import {
  getTreatmentName,
  getVariables,
  isViewMode,
  TreatmentDefaultColors,
} from '@/features/experiment/constants';
import {
  useExperimentContext,
  useExperimentDispatchContext,
} from '@/features/experiment/contexts/experiment-context';
import { updateConfiguration } from '@/features/experiment/store/actions/experiment/action-creators';
import { Constraint } from '@/lib/PlannerDTOs';
import GroupSelector from '@/pages/ExperimentFormV2/common/GroupSelector';

const ExplicitGroupValuesHeader = () => {
  const form = Form.useFormInstance();

  return (
    <thead>
      <tr>
        <th
          style={{
            padding: '4px 0',
            paddingRight: 16,
            background: '#FFF',
            borderRight: '1px solid #E8E8E8',
          }}
        >
          <div style={{ width: 320, textAlign: 'left' }} />
        </th>
        <Form.List name="configurations">
          {(configurations) =>
            configurations.map((configuration, idx: number) => {
              if (idx > 0) return null;

              return (
                <Form.List
                  name={[configuration.name, 'variables']}
                  key={configuration.key}
                >
                  {(variables) =>
                    variables.map((variable, index: number) => {
                      if (index > 0) return null;

                      return (
                        <Form.List
                          key={variable.key}
                          name={[variable.name, 'choices']}
                        >
                          {(choices) =>
                            choices.map((choice) => {
                              return (
                                <th
                                  key={choice.key}
                                  style={{
                                    padding: '4px 16px',
                                    borderRight: '1px solid #e8e8e8',
                                    position: 'relative',
                                    zIndex: 0,
                                    minWidth: 400,
                                    maxWidth: 400,
                                  }}
                                >
                                  <Tag
                                    color={`${TreatmentDefaultColors[choice.key]}1A`}
                                    style={{
                                      padding: '6px 16px',
                                      color: '#3D3D3D',
                                      margin: 0,
                                      width: '100%',
                                      minHeight: 40,
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 8,
                                        }}
                                      >
                                        <TreatmentDot
                                          color={
                                            TreatmentDefaultColors[choice.key]
                                          }
                                        />
                                        {getTreatmentName(
                                          form.getFieldValue([
                                            'configurations',
                                            configuration.name,
                                            'variables',
                                            variable.name,
                                            'choices',
                                            choice.name,
                                          ]),
                                          choice.key,
                                        )}
                                      </div>
                                    </div>
                                  </Tag>
                                </th>
                              );
                            })
                          }
                        </Form.List>
                      );
                    })
                  }
                </Form.List>
              );
            })
          }
        </Form.List>
      </tr>
    </thead>
  );
};

const ExplicitGroupValuesBody = () => {
  const params = useParams<{ action: string }>();
  const { action } = params;
  const readOnly = isViewMode(action);

  const experimentContext = useExperimentContext();
  const experimentDispatch = useExperimentDispatchContext();
  const { configurations: experimentConfigurations } = experimentContext;
  const variables = getVariables(experimentConfigurations);
  const treatments = variables?.[0]?.choices;

  const generateConstraintForTreatment = (idx: number): Constraint => {
    return treatments?.[idx]?.groupConstraint;
  };

  return (
    <tbody>
      <tr>
        <td
          style={{
            padding: '4px 0',
            paddingRight: 16,
            background: '#FFF',
            borderRight: '1px solid #E8E8E8',
          }}
        >
          <div style={{ width: 320, textAlign: 'left' }} />
        </td>
        <Form.List name="configurations">
          {(configurations) =>
            configurations.map((configuration, idx: number) => {
              if (idx > 0) return null;

              return (
                <Form.List
                  name={[configuration.name, 'variables']}
                  key={configuration.key}
                >
                  {(variables) =>
                    variables.map((variable, index: number) => {
                      if (index > 0) return null;

                      return (
                        <Form.List
                          key={variable.key}
                          name={[variable.name, 'choices']}
                        >
                          {(choices) =>
                            choices.map((choice) => {
                              return (
                                <td
                                  key={choice.key}
                                  style={{
                                    padding: '4px 16px',
                                    borderRight: '1px solid #e8e8e8',
                                    position: 'relative',
                                    zIndex: 0,
                                    minWidth: 400,
                                    maxWidth: 400,
                                  }}
                                >
                                  <GroupSelector
                                    disableInlineGroup
                                    mode={null}
                                    constraint={generateConstraintForTreatment(
                                      choice.key,
                                    )}
                                    onChange={(constraint: Constraint) => {
                                      const nextConfigurations = produce(
                                        experimentConfigurations,
                                        (draft) => {
                                          draft.forEach((c) => {
                                            c.variables.forEach((v) => {
                                              v.choices.forEach((ch, chidx) => {
                                                if (choice.name === chidx) {
                                                  ch['groupConstraint'] =
                                                    constraint;
                                                }
                                              });
                                            });
                                          });
                                        },
                                      );
                                      experimentDispatch(
                                        updateConfiguration(nextConfigurations),
                                      );
                                    }}
                                    disabled={readOnly}
                                  />
                                </td>
                              );
                            })
                          }
                        </Form.List>
                      );
                    })
                  }
                </Form.List>
              );
            })
          }
        </Form.List>
      </tr>
    </tbody>
  );
};

const ExplicitGroupValues = () => {
  return (
    <Card>
      <div style={{ width: '100%', overflowX: 'auto' }}>
        <table className="experiment-treatment-table">
          <ExplicitGroupValuesHeader />
          <ExplicitGroupValuesBody />
        </table>
      </div>
    </Card>
  );
};

export const TreatmentDot = styled.div<{ color: string }>`
  border-radius: 16px;
  height: 16px;
  width: 16px;
  line-height: 0px;
  background-color: ${({ color }: { color: string }) => color};
`;

const Tag = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  height: 40px;
  border-radius: 8px;
  padding: 12px 16px;
  background-color: ${({ color }: { color: string }) => color};
`;

export default ExplicitGroupValues;
